import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { startCase } from 'lodash';
import { Row } from 'spw-shared/globalComponents/grid';
import { useHistory } from 'react-router-v5';
import { Star, StarBorder } from '@mui/icons-material';
import {
  NO_DATA_TO_SHOW,
  PROJECTS_ROUTE,
  PROJECT_TYPE_TO_PATH,
} from 'spw-constants';
import { colors } from 'spw-theme';
import { Column, SPWProjectTypeTag } from 'spw-shared/globalComponents';
import {
  Column as ColumnType,
  SortBy,
  SortingDirection,
} from 'spw-components/collab/types';
import {
  getAriaLabelForSensitiveInfo,
  getLastEditorName,
  parseAsLocalDate,
} from 'spw-shared/utils';
import { OnListEndReachedCallback } from 'spw-shared/hooks/use-on-scrolled-until-bottom';
import SortButton from 'spw-components/collab/sort-button';
import ScrollableTable from 'spw-components/collab/scrollable-table';
import InfoButton, { InfoItemVM } from 'spw-components/collab/info-button';
import { IProject } from 'spw-redux/modules/projects/types';
import { Box, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ListActionButton from 'spw-components/collab/list-action-button';
import { getUserPendingProjectRequests } from 'spw-redux/modules/join-requests/actions';
import { selectedUserPendingProjectRequestsMap } from 'spw-redux/modules/join-requests/selectors';
import {
  createUserFavoriteProject,
  deleteUserFavoriteProject,
} from 'spw-redux/modules/projects/actions';
import { showToastMessage } from 'spw-redux/modules/UI/messages/actions';

import CloneIcon from 'spw-static/images/clone-icon.svg';
import JoinRequestPendingIcon from 'spw-static/images/join-request-pending-icon.svg';
import ProjectStatusTag from './project-status-tag';
import ProjectDescription from './components/project-description';
import LeaveProjectDialog from './leave-project-dialog';

const defaultStyles = {
  tableContainer: {
    '& .info': {
      fontSize: 14,
      lineHeight: '20px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
    '& .secondary-info': {
      fontSize: 12,
      lineHeight: '16px',
      color: colors.mainGray,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      width: '100%',
      textOverflow: 'ellipsis',
    },
    '& .MuiTableHead-root tr th': {
      border: 'none',
    },
    '& .MuiTableCell-head': {
      alignItems: 'center',
    },
    '& .MuiTableCell-head:first-child': {
      width: '14%',
    },
    '& .MuiTableCell-head:nth-child(2)': {
      width: '14%',
    },
    '& .MuiTableCell-head:last-child': {
      width: '10%',
    },
    '& .MuiTableCell-body > div': {
      alignItems: 'start',
    },
    '& .toggle-favorite-button': {
      cursor: 'pointer',
      color: colors.mainBlue,
      marginLeft: '14px',
    },
    '& .join-request-pending-chip': {
      display: 'flex',
      alignItems: 'center',
      fontSize: '12px',
      fontWeight: '400',
      lineHeight: '16px',
      color: colors.fontColor,
      '& img': {
        marginRight: '4px',
      },
    },
    '& .MuiIconButton-root:hover': {
      backgroundColor: 'unset',
    },
  },
  iconButton: {
    paddingTop: 0,
    paddingBottom: 0,
  },
};

const useStyles = makeStyles(defaultStyles);

const useExtendedTableStyles = makeStyles({
  ...defaultStyles,
  tableContainer: {
    ...defaultStyles.tableContainer,
    '& .MuiTableCell-head:first-child': {
      width: '5%',
    },
    '& .MuiTableCell-head:nth-child(2)': {
      width: '16%',
      paddingLeft: '16px',
      paddingRight: '0px',
    },
    '& .MuiTableCell-head:nth-child(3)': {
      width: '14%',
    },
    '& .MuiTableCell-head:nth-child(4)': {
      padding: '16px 0px',
      width: '11%',
    },
    '& .MuiTableCell-head:nth-child(5)': {
      padding: '16px 0px',
      width: '11%',
    },
    '& .MuiTableCell-head:nth-child(6)': {
      width: '13%',
    },
    '& .MuiTableCell-head:nth-child(7)': {
      padding: '16px 0px',
      width: '10%',
    },
    '& .MuiTableCell-head:nth-child(8)': {
      padding: '16px 0px 16px 16px',
      width: '10%',
    },
    '& .MuiTableCell-body:nth-child(8)': {
      padding: '10px 0px 9px 16px',
    },
    '& .MuiTableCell-head:last-child': {
      width: '10%',
    },
    '& .MuiTableCell-body:last-child:has(div.actions-column--padding)': {
      paddingRight: 0,
    },
  },
});

export type ProjectVM = IProject & {
  action?: string;
};

type ProjectsTableColumns =
  | 'isMarkedAsFavoriteByUser'
  | 'client'
  | 'projectName'
  | 'type'
  | 'status'
  | 'createdDate'
  | 'lastModified'
  | 'projectOwner';

type Props = {
  projects: ProjectVM[];
  sortBy?: SortBy;
  onSortChange?: (newSort: SortBy) => void;
  infoItems?: InfoItemVM[];
  onScrolledUntilBottom?: OnListEndReachedCallback;
  itemsPerPage?: number;
  onProjectActionClick?: (project: ProjectVM, action: string) => void;
  disabledColumns?: { [key in ProjectsTableColumns]?: boolean };
  disableCloning?: boolean;
  onToggledFavorite?: (projectId: number, newValue: boolean) => void;
};

const context = 'Projects List Row';

const ProjectsList = ({
  projects,
  infoItems,
  onScrolledUntilBottom,
  itemsPerPage,
  onSortChange,
  sortBy,
  onProjectActionClick,
  disabledColumns,
  disableCloning = false,
  onToggledFavorite,
}: Props) => {
  const classes = disabledColumns?.['isMarkedAsFavoriteByUser']
    ? useStyles()
    : useExtendedTableStyles();

  const history = useHistory();
  const userPendingProjectRequestsMap = useSelector(
    selectedUserPendingProjectRequestsMap
  );
  const dispatch = useDispatch();
  const [
    isLeaveProjectDialogVisible,
    setIsLeaveProjectDialogVisible,
  ] = useState(false);
  const [selectedProject, setSelectedProject] = useState<
    ProjectVM | undefined
  >();

  useEffect(() => {
    dispatch(getUserPendingProjectRequests());
  }, [dispatch]);

  const handleClickProject = (project: ProjectVM) => {
    history.push(
      `${PROJECTS_ROUTE}/${PROJECT_TYPE_TO_PATH[project.type]}/${
        project.id
      }/project-profile`
    );
  };

  const handleLeaveProjectClick = (project: ProjectVM) => {
    setSelectedProject(project);
    setIsLeaveProjectDialogVisible(true);
  };

  const handleLeaveProjectDialogClose = () => {
    setSelectedProject(undefined);
    setIsLeaveProjectDialogVisible(false);
  };

  const handleSortIconClick = useCallback(
    (value: string) => {
      let direction: SortingDirection;

      if (sortBy?.column === value && sortBy?.direction === 'ASC') {
        direction = 'DESC';
      } else {
        direction = 'ASC';
      }

      onSortChange({ column: value, direction });
    },
    [sortBy, onSortChange]
  );

  const handleLeaveProject = useCallback(() => {
    dispatch(
      showToastMessage({
        status: 'success',
        text: 'You have left the project',
      })
    );
  }, [dispatch]);

  const actionsColumnClass =
    Object.values(disabledColumns || {}).length === 0
      ? 'actions-column--padding'
      : '';

  const columns: ColumnType<ProjectVM>[] = useMemo(() => {
    const baseColumns: ColumnType<ProjectVM>[] = [
      {
        value: 'isMarkedAsFavoriteByUser',

        text: 'FAVORITES',
        sortable: true,
        align: 'left',
        render: ({ id, isMarkedAsFavoriteByUser }) => (
          <IconButton
            className={classes.iconButton}
            onClick={(e) => {
              e.stopPropagation();

              if (isMarkedAsFavoriteByUser) {
                dispatch(
                  deleteUserFavoriteProject({
                    projectId: id,
                    onSuccess: onToggledFavorite
                      ? () => onToggledFavorite(id, false)
                      : undefined,
                  })
                );
              } else {
                dispatch(
                  createUserFavoriteProject(
                    id,
                    {
                      shouldRefreshProjectsAfterwards: false,
                    },
                    onToggledFavorite
                      ? () => onToggledFavorite(id, true)
                      : undefined
                  )
                );
              }
            }}
          >
            {isMarkedAsFavoriteByUser ? (
              <Star className="toggle-favorite-button" />
            ) : (
              <StarBorder className="toggle-favorite-button" />
            )}
          </IconButton>
        ),
      },
      {
        value: 'projectName',
        text: 'PROJECT NAME & NO.',
        sortable: true,
        align: 'left',
        render: (project) => (
          <ProjectDescription
            dataTestId={`user_project_${project?.id}`}
            project={project}
            noLinkToProject
          />
        ),
      },
      {
        value: 'client',
        text: 'CLIENT NAME',
        sortable: true,
        align: 'left',
        render: ({ client }) => (
          <Column>
            <div
              className="info"
              aria-label={getAriaLabelForSensitiveInfo({
                context,
                desc: 'Client name',
                id: client?.id,
              })}
            >
              {client?.name}
            </div>
            <div className="secondary-info">{client?.industry?.name}</div>
          </Column>
        ),
      },
      {
        value: 'type',
        text: 'PROJECT TYPE',
        align: 'center',
        renderHeader: () => (
          <Row justifyContent="center" alignItems="center" width="100%">
            <div className="column-title margin-right-8">PROJECT TYPE</div>
            <SortButton
              sortBy={sortBy}
              value="type"
              onClick={() => handleSortIconClick('type')}
            />
          </Row>
        ),
        render: ({ type }) => (
          <Row width="100%" justifyContent="center">
            <Box marginRight="24px">
              <SPWProjectTypeTag projectType={type} />
            </Box>
          </Row>
        ),
      },
      {
        value: 'status',
        text: 'PROJECT STATUS',
        align: 'center',
        renderHeader: () => (
          <Row justifyContent="center" alignItems="center" width="100%">
            <div className="column-title margin-right-8">PROJECT STATUS</div>
            <SortButton
              sortBy={sortBy}
              value="status"
              onClick={() => handleSortIconClick('status')}
            />
          </Row>
        ),
        render: ({ status, hasMissingBenchmarkData }) => (
          <Row width="100%" justifyContent="center">
            <Box marginRight="24px">
              <ProjectStatusTag
                status={status}
                hasMissingBenchmarkData={hasMissingBenchmarkData}
              />
            </Box>
          </Row>
        ),
      },
      {
        value: 'projectOwner',
        text: 'PROJECT OWNER',
        className: 'info',
        align: 'left',
        sortable: true,
        render: ({ projectOwner }) => (
          <div
            className="info"
            aria-label={getAriaLabelForSensitiveInfo({
              context,
              desc: 'Project Owner',
              id: projectOwner?.id,
            })}
          >
            {projectOwner?.name || NO_DATA_TO_SHOW}
          </div>
        ),
      },
      {
        value: 'createdDate',
        text: 'CREATED DATE',
        className: 'info',
        align: 'center',
        renderHeader: () => (
          <Row justifyContent="center" alignItems="center" width="100%">
            <div className="column-title">CREATED DATE</div>
            <SortButton
              sortBy={sortBy}
              value="insertedAt"
              onClick={() => handleSortIconClick('insertedAt')}
            />
          </Row>
        ),
        render: ({ insertedAt }) => (
          <Row width="100%" justifyContent="center">
            <Box>{parseAsLocalDate(insertedAt, 'MM/DD/YYYY')}</Box>
          </Row>
        ),
      },
      {
        value: 'updatedAt',
        text: 'LAST MODIFIED',
        className: 'info',
        sortable: true,
        align: 'left',
        render: ({ lastEditor, updatedAt }) => (
          <Column>
            <div className="info">
              {parseAsLocalDate(updatedAt, 'MM/DD/YYYY')}
            </div>
            <div
              className="secondary-info"
              aria-label={getAriaLabelForSensitiveInfo({
                context,
                desc: 'Last Editor',
                id: lastEditor?.id,
              })}
            >
              {getLastEditorName(lastEditor)}
            </div>
          </Column>
        ),
      },
      {
        value: 'action',
        align: 'right',
        renderHeader: () => (
          <InfoButton
            transformOrigin={{
              horizontal: 'right',
              vertical: 'center',
            }}
            providedInfoList={infoItems}
          />
        ),
        render: (project) => {
          const { id, action, projectName } = project;
          const userAlreadySentProjectRequest =
            userPendingProjectRequestsMap[id];

          return action ? (
            <Row
              style={{ alignItems: 'center' }}
              className={actionsColumnClass}
              justifyContent="end"
            >
              {userAlreadySentProjectRequest ? (
                <div className="join-request-pending-chip">
                  <img
                    src={JoinRequestPendingIcon}
                    alt="join request pending"
                  />
                  PENDING
                </div>
              ) : (
                <ListActionButton
                  color={
                    ['ask-to-join', 'join'].includes(action.toLowerCase())
                      ? 'mainBlue'
                      : undefined
                  }
                  title={startCase(action).toUpperCase()}
                  onClick={() =>
                    action === 'leave'
                      ? handleLeaveProjectClick(project)
                      : onProjectActionClick(project, action)
                  }
                />
              )}
              {!disableCloning ? (
                <IconButton
                  className={classes.iconButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    onProjectActionClick(project, 'clone');
                  }}
                  data-testid={`clone ${projectName}`}
                >
                  <img src={CloneIcon} alt="clone project" />
                </IconButton>
              ) : null}
            </Row>
          ) : null;
        },
      },
    ];
    return baseColumns.filter(
      (c) =>
        !disabledColumns || !disabledColumns[c.value as ProjectsTableColumns]
    );
  }, [
    classes,
    onProjectActionClick,
    infoItems,
    sortBy,
    handleSortIconClick,
    disabledColumns,
    disableCloning,
    userPendingProjectRequestsMap,
    onToggledFavorite,
    actionsColumnClass,
    dispatch,
  ]);

  return (
    <>
      <ScrollableTable<ProjectVM>
        containerClassName={classes.tableContainer}
        columns={columns}
        items={projects}
        itemsPerPage={itemsPerPage}
        loading={false}
        onScrolledUntilBottom={onScrolledUntilBottom}
        onSortChange={onSortChange}
        onTableRowClick={handleClickProject}
        smallPaddingOnLastColumn
        sortBy={sortBy}
        sx={{ tableLayout: 'fixed', minWidth: '1350px' }}
      />
      <LeaveProjectDialog
        isVisible={isLeaveProjectDialogVisible}
        onClose={handleLeaveProjectDialogClose}
        project={selectedProject}
        onLeaveProjectSuccess={handleLeaveProject}
      />
    </>
  );
};

export default ProjectsList;
